import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

export interface AuthorizedApp {
  id: string;
  client_name: string;
  client_logo_url: string | null;
  scopes: string[];
  created_at: string;
}

export interface OAuthClient {
  id: string;
  client_id: string;
  name: string;
  redirect_uris: string[];
  scopes: string[];
  logo_url: string | null;
  website_url: string | null;
  created_at: string;
}

export interface RegisterClientResponse extends OAuthClient {
  client_secret: string;
}

// ─── Connected Apps (user) ──────────────────────────────────

export function useAuthorizedApps() {
  return useQuery<AuthorizedApp[]>({
    queryKey: ['oauth-apps'],
    queryFn: async () => {
      const res = await api.get('/oauth/apps');
      return res.data;
    },
  });
}

export function useRevokeApp() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await api.delete(`/oauth/apps/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['oauth-apps'] });
    },
  });
}

// ─── Developer: OAuth Clients ───────────────────────────────

export function useOAuthClients() {
  return useQuery<OAuthClient[]>({
    queryKey: ['oauth-clients'],
    queryFn: async () => {
      const res = await api.get('/oauth/clients');
      return res.data;
    },
  });
}

export function useRegisterClient() {
  const qc = useQueryClient();
  return useMutation<
    RegisterClientResponse,
    Error,
    { name: string; redirect_uris: string[]; scopes: string[]; logo_url?: string; website_url?: string }
  >({
    mutationFn: async (body) => {
      const res = await api.post('/oauth/clients', body);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['oauth-clients'] });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await api.delete(`/oauth/clients/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['oauth-clients'] });
    },
  });
}
